import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { TAARenderPass } from 'three/examples/jsm/postprocessing/TAARenderPass';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass';

import { createScene } from './core/scene';
import { createCamera, resetCameraPosition } from './core/camera';
import { createRenderer, resizeRenderer } from './core/renderer';
import { createControls } from './core/controls';
import { setupEnvironment } from './core/lights';

import { loadGLTF } from './loaders/gltfLoader';

import { getState, setState } from './state';
import { disposeObject } from './utils/disposer';
import { applyColorToModel } from './utils/modelHelper';

import { createLeftPanel } from './ui/leftPanel';
import { createRightPanel } from './ui/rightPanel';
import { createLoadingOverlay, createVariantLoader } from './ui/loadingOverlay';
import { createMeasurements, MeasurementOverlay }   from './ui/measurements';
import { createDebugGui } from './ui/debugGui';
import { initGuide } from './ui/guide';

import { ConfiguratorType, ConfiguratorModule, Hotspot } from './types';
import furniture from './configurators/furniture';
import accessories from './configurators/accessories';
import automobile from './configurators/automobile';

// ─── Registry ──────────────────────────────────────────────────────────────

const CONFIGURATORS: Record<ConfiguratorType, ConfiguratorModule> = {
  furniture,
  accessories,
  automobile,
};

// ─── App ───────────────────────────────────────────────────────────────────

/**
 * Self-contained 3D configurator.
 *
 * Usage:
 *   const app = new ConfiguratorApp(document.getElementById('my-slot'));
 *   // Later:
 *   app.destroy();
 */
export class ConfiguratorApp {
  // Three.js core
  private scene!: THREE.Scene;
  private spriteScene!: THREE.Scene; // isolated scene for hotspot sprites — bypasses post-processing
  private camera!: THREE.PerspectiveCamera;
  private renderer!: THREE.WebGLRenderer;
  private controls!: OrbitControls;
  private composer!: EffectComposer;
  private taaPass!: TAARenderPass;
  private gtaoPass!: GTAOPass;
  private bokehPass!: BokehPass;
  private smaaPass!: SMAAPass;
  private _aaMode: 'off' | 'smaa' | 'tsaa' = 'tsaa';

  // DOM refs
  private centerEl!: HTMLElement;
  private canvas!: HTMLCanvasElement;

  // UI helpers
  private showLoading!:        () => void;
  private hideLoading!:        () => void;
  private showVariantLoader!:  () => void;
  private hideVariantLoader!:  () => void;

  // Cross-fade morph between variant models
  private _morphOldModel:   THREE.Object3D | null = null;
  private _morphNewModel:   THREE.Object3D | null = null;
  private _morphFadeIn  = 0; // new model opacity: 0 → 1 (old stays fully opaque as backdrop)
  private _morphActive  = false;

  // Frames remaining to force taaPass.accumulate = false after a scene change.
  // Guarantees the stale accumulated buffer is never blended into the fresh scene.
  private _taaResetFrames = 0;

  // Loop / resize
  private rafId: number | null = null;
  private resizeObserver!: ResizeObserver;

  // Fullscreen — bound reference kept for removeEventListener in destroy()
  private readonly _onFullscreenChange = (): void => this.onFullscreenChange();

  // Double-click / double-tap to focus
  private readonly _raycaster = new THREE.Raycaster();
  private _autoFocusDist      = 5;   // smoothed world-space focal distance (autofocus)
  private _prevCamX = 0; private _prevCamY = 0; private _prevCamZ = 0; // TAA motion detection
  private _inFocalMode        = false; // true only while a double-click focus is active
  private _dofApertureTarget  = 0;     // desired aperture — set by focus/reset transitions
  private _dofApertureCurrent = 0;     // smoothly lerped toward target each frame

  // Camera fly-to animation — driven from the main render loop (after controls.update)
  // so that OrbitControls never overrides the lerp mid-frame.
  private _focusAnim: {
    fromTarget: THREE.Vector3;
    toTarget:   THREE.Vector3;
    fromCamPos: THREE.Vector3;
    toCamPos:   THREE.Vector3;
    startTime:  number;
    duration:   number;
    onComplete: () => void;
  } | null = null;
  // Rotation-hint animation (plays once after guide closes)
  private _hintAnim: {
    baseCamPos: THREE.Vector3; // camera position at animation start
    pivot:      THREE.Vector3; // orbit target (stays fixed)
    startTime:  number;
    duration:   number;
    arrowEl:    HTMLElement;
  } | null = null;

  private readonly _onDblClick  = (e: MouseEvent):  void => this.onDoubleClick(e);
  private readonly _onTouchEnd  = (e: TouchEvent):  void => this.onTouchEnd(e);
  private _lastTapTime = 0;
  private _lastTapX    = 0;
  private _lastTapY    = 0;

  // Hotspots — Three.js sprites living in the scene
  private _hotspots: Array<{
    label:    string;
    worldPos: THREE.Vector3;
    dot:      THREE.Sprite;
    ring:     THREE.Sprite;
  }> = [];
  private _hotspotInfoEl: HTMLElement | null = null;
  private _hotspotDisposables: Array<THREE.CanvasTexture | THREE.SpriteMaterial> = [];
  /** 0 = fully hidden, 1 = fully visible — driven by lerp each frame */
  private _hotspotT     = 0;
  /** Set false when loading starts; true when buildHotspots() completes */
  private _hotspotReady  = false;
  /** True while the first-load navigation guide is on screen; hotspots wait for it to close */
  private _guideActive   = false;
  private readonly _onClick = (e: MouseEvent): void => this.onSingleClick(e);

  // Debug GUI per-frame updater (camera position display)
  private _debugUpdate: (() => void) | null = null;

  // Guide overlay shown once on first model load
  private _guideShown = false;

  // Load-time camera snapshot — used for max-zoom-out constraint + "View Full Model" reset
  private readonly _loadCamPos = new THREE.Vector3();
  private readonly _loadTarget  = new THREE.Vector3();
  private _loadMinDist = 0; // minDistance at load time; restored when resetting focus
  private _loadMaxDist = 0; // maxDistance at load time; restored when resetting focus
  private _focusResetBtn:      HTMLButtonElement | null = null;
  private _measureBtn:         HTMLButtonElement | null = null;
  private _measureOverlay:     MeasurementOverlay | null = null;
  private _measurementsVisible = false;
  private _hotspotBtn:         HTMLButtonElement | null = null;
  private _hotspotsEnabled     = true;
  private _dropShadow:         THREE.Mesh | null = null;
  private _comingSoonEl:       HTMLElement | null = null;

  constructor(private readonly container: HTMLElement) {
    this.buildDOM();
    this.initThree();
    this.initDebugGui();
    this.bindResize();
    this.loadModel('furniture');
    this.startLoop();
  }

  // ── DOM layout ────────────────────────────────────────────────────────────

  private buildDOM(): void {
    // The container becomes the flex root
    this.container.classList.add('cfg-wrapper');

    // Left panel
    createLeftPanel(this.container, (type) => this.switchTo(type));

    // Centre — canvas + loading overlay
    const center = document.createElement('div');
    center.className = 'cfg-center';

    const canvas = document.createElement('canvas');
    canvas.className = 'cfg-canvas';
    center.appendChild(canvas);

    const { show, hide } = createLoadingOverlay(center);
    this.showLoading = show;
    this.hideLoading = hide;

    const { show: showVL, hide: hideVL } = createVariantLoader(center);
    this.showVariantLoader = showVL;
    this.hideVariantLoader = hideVL;

    // "View Full Model" overlay — shown only after a double-click focus
    const focusResetBtn = document.createElement('button');
    focusResetBtn.className = 'cfg-focus-reset-btn';
    focusResetBtn.textContent = 'View Full Model';
    focusResetBtn.style.display = 'none';
    focusResetBtn.addEventListener('click', () => this.resetFocus());
    center.appendChild(focusResetBtn);
    this._focusResetBtn = focusResetBtn;

    // Bottom-left tool buttons — flex row so they stack naturally
    const toolRow = document.createElement('div');
    toolRow.className = 'cfg-tool-row';

    const measureBtn = document.createElement('button');
    measureBtn.className = 'cfg-measure-btn';
    measureBtn.title = 'Toggle dimensions';
    measureBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
      <path d="M2 12h20M2 12l3-3M2 12l3 3M22 12l-3-3M22 12l-3 3"/>
      <path d="M12 2v20M12 2l-3 3M12 2l3 3M12 22l-3-3M12 22l3-3"/>
    </svg><span>Dimensions</span>`;
    measureBtn.addEventListener('click', () => this.toggleMeasurements());
    this._measureBtn = measureBtn;

    const hotspotBtn = document.createElement('button');
    hotspotBtn.className = 'cfg-measure-btn cfg-measure-btn--active'; // active = visible by default
    hotspotBtn.title = 'Toggle hotspots';
    hotspotBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <circle cx="12" cy="12" r="7" stroke-dasharray="2 2.5" opacity="0.6"/>
    </svg><span>Hotspots</span>`;
    hotspotBtn.addEventListener('click', () => this.toggleHotspots());
    this._hotspotBtn = hotspotBtn;

    toolRow.appendChild(measureBtn);
    toolRow.appendChild(hotspotBtn);
    center.appendChild(toolRow);

    this.container.appendChild(center);
    this.centerEl = center;
    this.canvas = canvas;
    this.canvas.addEventListener('click',     this._onClick);
    this.canvas.addEventListener('dblclick',  this._onDblClick);
    this.canvas.addEventListener('touchend',  this._onTouchEnd, { passive: false });

    // Right panel — floating overlay inside the canvas area.
    createRightPanel(this.centerEl, CONFIGURATORS, () => {
      this.loadModel(getState().activeConfigurator, true);
    });

    // Wire strip action buttons (always in DOM)
    this.centerEl.querySelector<HTMLButtonElement>('#cfg-reset-camera-strip')
      ?.addEventListener('click', () => this.resetCamera());
    this.centerEl.querySelector<HTMLButtonElement>('#cfg-fullscreen-strip')
      ?.addEventListener('click', () => this.toggleFullscreen());

    // Wire panel buttons and AA select via delegation — these are rendered
    // dynamically when the flyout or full panel opens.
    this.centerEl.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('#cfg-reset-camera'))   this.resetCamera();
      if (t.closest('#cfg-fullscreen-btn')) this.toggleFullscreen();
    });
    this.centerEl.addEventListener('change', (e) => {
      const t = e.target as HTMLSelectElement;
      if (t.id === 'cfg-aa-select') this.setAAMode(t.value as 'off' | 'smaa' | 'tsaa');
    });

    // Keep button label in sync when the user exits via Escape key
    document.addEventListener('fullscreenchange', this._onFullscreenChange);
  }

  // ── Three.js bootstrap ────────────────────────────────────────────────────

  private initThree(): void {
    const { clientWidth: w, clientHeight: h } = this.centerEl;

    this.scene       = createScene();
    this.spriteScene = new THREE.Scene(); // sprites live here, never touched by post-processing
    this.camera = createCamera(w || 1, h || 1);
    this.renderer = createRenderer(this.canvas);
    this.controls = createControls(this.camera, this.canvas);

setupEnvironment(this.scene, this.renderer); // async — applies when HDR finishes loading

    // Post-processing pipeline
    // HalfFloat render target gives 16-bit colour precision throughout the chain,
    // reducing banding in gradients and improving DoF / AO quality.
    const composerTarget = new THREE.WebGLRenderTarget(w || 1, h || 1, {
      type: THREE.HalfFloatType,
    });
    this.composer = new EffectComposer(this.renderer, composerTarget);

    this.taaPass = new TAARenderPass(this.scene, this.camera);
    this.taaPass.sampleLevel   = 4;     // 16 jittered samples (2^4) — max TAA quality
    this.taaPass.accumulate    = false; // render loop controls this per-frame
    this.composer.addPass(this.taaPass);

    // GTAO — screen-space AO that fires hemispherical rays per pixel to
    // approximate how global illumination occludes ambient light in crevices.
    // Higher sample counts ≈ more ray directions evaluated = closer to path-traced AO.
    this.gtaoPass = new GTAOPass(this.scene, this.camera, w || 512, h || 512);
    this.gtaoPass.blendIntensity = 1.2;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const gtao = this.gtaoPass as any;
    gtao.updateGtaoMaterial({
      samples:         16,   // hemispherical ray directions — more = closer to path-traced AO
      radius:          0.3,  // world-space AO radius, tight for furniture scale
      distanceFallOff: 1.0,
      thickness:       1.0,  // sharper contact shadows
    });
    gtao.updatePdMaterial({
      lumaPhi:  10,  // luma edge-stop weight
      depthPhi:  2,  // depth edge-stop — reduces AO bleeding across silhouettes
      normalPhi: 3,  // normal edge-stop
      radius:    4,  // Poisson denoise kernel radius (px)
      rings:     2,
      samples:  16,
    });
    this.composer.addPass(this.gtaoPass);

    this.bokehPass = new BokehPass(this.scene, this.camera, {
      focus:    5,
      aperture: 0,     // starts at 0 (no blur); driven by updateDoF every frame
      maxblur:  0.025,
    });
    this.composer.addPass(this.bokehPass);

    // SMAA — Subpixel Morphological AA. Runs every frame (even while moving),
    // complementing TAA which only accumulates while the camera is still.
    // SMAA uses three-pass edge detection + blending weights + neighbourhood blend
    // and produces sharper, more temporally stable edges than FXAA.
    this.smaaPass = new SMAAPass(w || 1, h || 1);
    this.smaaPass.enabled = false; // setAAMode enables it for 'smaa' and 'tsaa'
    this.composer.addPass(this.smaaPass);

    this.composer.addPass(new OutputPass()); // tone mapping + sRGB encoding
  }

  // ── Debug GUI ─────────────────────────────────────────────────────────────

  private initDebugGui(): void {
    const { element, update } = createDebugGui(this.centerEl, {
      renderer:  this.renderer,
      scene:     this.scene,
      camera:    this.camera,
      controls:  this.controls,
      bokehPass: this.bokehPass,
      gtaoPass:  this.gtaoPass,
      taaPass:   this.taaPass,
      getAAMode: () => this._aaMode,
      setAAMode: (mode) => this.setAAMode(mode),
    });
    element.style.display = 'none';
    this._debugUpdate = update;
  }

  // ── Resize ────────────────────────────────────────────────────────────────

  private bindResize(): void {
    // requestAnimationFrame defers the callback to the next paint, preventing
    // the "ResizeObserver loop completed with undelivered notifications" error
    // that fires when fullscreen entry/exit triggers a synchronous resize cascade.
    this.resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => this.onResize());
    });
    this.resizeObserver.observe(this.centerEl);
    this.onResize(); // run once to match initial container size
  }

  private onResize(): void {
    const w = this.centerEl.clientWidth;
    const h = this.centerEl.clientHeight;
    if (w === 0 || h === 0) return;
    resizeRenderer(this.renderer, this.camera, w, h);
    this.composer.setSize(w, h);
    this.gtaoPass.setSize(w, h);
    // FXAA needs pixel-exact resolution to sample correctly
    this.smaaPass.setSize(w, h);
    this._measureOverlay?.setSize(w, h);
    this.invalidateTAA(); // accumulated buffer size changed — must be re-created
  }

  // ── Model loading ─────────────────────────────────────────────────────────

  private async loadModel(type: ConfiguratorType, isVariantSwap = false): Promise<void> {
    if (getState().isLoading) return;

    setState({ isLoading: true });
    this._hotspotReady = false; // hide hotspots immediately while loading
    if (isVariantSwap) {
      this.showVariantLoader();
    } else {
      this.showLoading();
    }

    const config = CONFIGURATORS[type];
    let model: THREE.Object3D;

    // Prefer the variant-specific model path when one exists for the active variant
    const activeVariant = type === 'furniture' ? getState().furnitureVariant : undefined;
    const resolvedPath  = (activeVariant && config.variantModelPaths?.[activeVariant])
                          ?? config.modelPath;

    try {
      if (resolvedPath) {
        model = await loadGLTF(resolvedPath);
      } else {
        // Slight delay so the loading indicator is visible even for instant builds
        await new Promise<void>((r) => setTimeout(r, 350));
        model = config.buildPlaceholder();
      }
    } catch (err) {
      console.warn('[Configurator] Failed to load GLTF; using placeholder.', err);
      model = config.buildPlaceholder();
    }

    // Remove previous model — for variant swaps keep it in the scene temporarily
    // so the cross-fade morph can fade it out alongside the incoming model.
    const { currentModel } = getState();
    if (currentModel) {
      if (isVariantSwap) {
        // Abort any in-progress morph and dispose its stale old model
        if (this._morphActive && this._morphOldModel && this._morphOldModel !== currentModel) {
          this.scene.remove(this._morphOldModel);
          disposeObject(this._morphOldModel);
        }
        this._morphOldModel = currentModel;
      } else {
        this.scene.remove(currentModel);
        disposeObject(currentModel);
      }
    }

    // Snapshot current camera state — used by variant swaps to keep the camera still.
    const preCamPos  = this._loadCamPos.clone();
    const preTarget  = this._loadTarget.clone();
    const preMinDist = this._loadMinDist;
    const preMaxDist = this._loadMaxDist;

    if (isVariantSwap && preMaxDist > 0) {
      // ── Variant swap: skip positionModelAndCamera entirely. ─────────────────
      // Instead: scale the new model to 2.5 units and plant its center at
      // preTarget so the camera (which we restore unchanged) is already
      // aimed at the correct point. This avoids the re-centre / camera-reset
      // cycle that caused the "zoomed-in" artefact.

      // 1. Rotation — must happen before bounding-box measurement
      if (config.modelRotationY !== undefined) model.rotation.y = config.modelRotationY;

      // 2. Scale to 2.5-unit max (same formula as positionModelAndCamera)
      model.updateWorldMatrix(true, true);
      const swapRawBox  = new THREE.Box3().setFromObject(model);
      const swapRawSize = swapRawBox.getSize(new THREE.Vector3());
      const swapRawMax  = Math.max(swapRawSize.x, swapRawSize.y, swapRawSize.z);
      model.scale.setScalar(swapRawMax > 0 ? 2.5 / swapRawMax : 1);

      // 3. Floor the model and centre it at world origin
      model.updateWorldMatrix(true, true);
      const swapScaledBox    = new THREE.Box3().setFromObject(model);
      const swapScaledCenter = swapScaledBox.getCenter(new THREE.Vector3());
      model.position.x -= swapScaledCenter.x;
      model.position.y -= swapScaledBox.min.y;
      model.position.z -= swapScaledCenter.z;

      // 4. Translate so model centre aligns with preTarget (x/z only; y stays floored)
      model.updateWorldMatrix(true, true);
      const swapFinalCenter = new THREE.Box3().setFromObject(model).getCenter(new THREE.Vector3());
      model.position.x += preTarget.x - swapFinalCenter.x;
      model.position.z += preTarget.z - swapFinalCenter.z;

      // 5. Restore camera exactly — no controls.update() call here; the render
      //    loop will sync OrbitControls from the freshly set position + target.
      this.camera.position.copy(preCamPos);
      this.controls.target.copy(preTarget);
      this.controls.minDistance    = preMinDist;
      this.controls.maxDistance    = preMaxDist;
      this._loadCamPos.copy(preCamPos);
      this._loadTarget.copy(preTarget);
      this._loadMinDist            = preMinDist;
      this._loadMaxDist            = preMaxDist;
      this._dofApertureTarget      = 0;
      this._dofApertureCurrent     = 0;
      this.controls.saveState();

      // 6. Reset focal mode + rebuild hotspots (normally done inside positionModelAndCamera)
      if (this._focusResetBtn) this._focusResetBtn.style.display = 'none';
      this._inFocalMode = false;
      if (config.hotspots?.length) {
        this.buildHotspots(config.hotspots, preTarget.clone());
      } else {
        this.clearHotspots();
      }
    } else {
      // Position + camera — uses cameraTarget when set (furniture) so the
      // bounding-box reference and orbit target are scoped to the named node.
      this.positionModelAndCamera(model, config);
    }

    // Colour — use the configurator's own applyColor (with material cloning)
    // for furniture; fall back to the generic utility for everything else.
    if (config.applyColor) {
      config.applyColor(model, getState().furnitureColor);
    } else {
      applyColorToModel(model, getState().currentColor);
    }

    // Variant — only apply material tweaks when the variant does NOT have its
    // own model path (model-per-variant needs no extra material adjustments).
    const loadedVariant = type === 'furniture' ? getState().furnitureVariant : undefined;
    const variantHasOwnModel = loadedVariant && !!config.variantModelPaths?.[loadedVariant];
    if (config.applyVariant && !variantHasOwnModel) {
      config.applyVariant(model, loadedVariant!);
    }

    this.scene.add(model);
    setState({ currentModel: model, activeConfigurator: type, isLoading: false });

    if (isVariantSwap && this._morphOldModel) {
      // Old model stays fully opaque as backdrop; new model fades in on top.
      // Avoids two semi-transparent meshes causing depth-sort bleed-through.
      this._setModelOpacity(model, 0);
      this._morphNewModel = model;
      this._morphFadeIn   = 0;
      this._morphActive   = true;
      // Variant loader hides itself once morph completes (in tickMorph)
    } else {
      this.hideLoading();
      this.invalidateTAA(); // new model — flush stale accumulated frames
    }

    // Build dimension annotations (always — visibility controlled by toggle)
    this.buildMeasurements(model);
    this.buildDropShadow(model);

    if (!this._guideShown) {
      this._guideShown  = true;
      this._guideActive = true;
      initGuide(this.centerEl, () => {
        this._guideActive = false; // hotspots can now appear
        this.playRotationHint();
      });
    }
  }

  // ── Public helpers ────────────────────────────────────────────────────────

  private switchTo(type: ConfiguratorType): void {
    if (getState().isLoading || getState().activeConfigurator === type) return;

    if (type === 'accessories' || type === 'automobile') {
      this.showComingSoon(type);
      return;
    }

    this.hideComingSoon();
    this.loadModel(type);
  }

  private showComingSoon(type: ConfiguratorType): void {
    this.hideComingSoon();

    // Update state so the left panel highlights the selected tab
    setState({ activeConfigurator: type });

    // Hide canvas and UI chrome
    this.canvas.style.visibility = 'hidden';
    const toolRow = this.centerEl.querySelector<HTMLElement>('.cfg-tool-row');
    if (toolRow) toolRow.style.visibility = 'hidden';
    const strip = this.centerEl.querySelector<HTMLElement>('.cfg-strip');
    if (strip) strip.style.visibility = 'hidden';

    const label = type === 'automobile' ? 'Automobile' : 'Accessories';

    const el = document.createElement('div');
    el.className = 'cfg-coming-soon';
    el.innerHTML = `
      <p class="cfg-coming-soon__title">Coming Soon</p>
      <p class="cfg-coming-soon__sub">${label} configurator is under construction.<br>Check back shortly.</p>
    `;
    this.centerEl.appendChild(el);
    this._comingSoonEl = el;
    requestAnimationFrame(() => el.classList.add('cfg-coming-soon--visible'));
  }

  private hideComingSoon(): void {
    const el = this._comingSoonEl;
    if (!el) return;
    this._comingSoonEl = null;

    // Restore canvas and UI chrome
    this.canvas.style.visibility = '';
    const toolRow = this.centerEl.querySelector<HTMLElement>('.cfg-tool-row');
    if (toolRow) toolRow.style.visibility = '';
    const strip = this.centerEl.querySelector<HTMLElement>('.cfg-strip');
    if (strip) strip.style.visibility = '';

    el.classList.remove('cfg-coming-soon--visible');
    el.addEventListener('transitionend', () => el.remove(), { once: true });
  }

  private resetCamera(): void {
    resetCameraPosition(this.camera);
    this.controls.reset(); // restores saved target + zoom
    this.controls.update();
  }

  // ── Model positioning + camera focus ──────────────────────────────────────

  /**
   * Centers, scales, and aims the camera for a freshly loaded model.
   *
   * When config.cameraTarget is set (e.g. 'seat' for the leather chair):
   *   1. Scale is derived from the NAMED node's bounding box, not the whole
   *      model — prevents extra geometry (ground planes, helpers) from
   *      causing the real object to appear tiny.
   *   2. The model is floored and centered on the named node's footprint.
   *   3. OrbitControls target is set to the node's center and saved.
   *
   * For all other configurators: falls back to the generic centerAndScale
   * and resets camera to the standard position.
   */
  private positionModelAndCamera(
    model: THREE.Object3D,
    config: { cameraTarget?: string; modelRotationY?: number; hotspots?: Hotspot[] }
  ): void {
    // Hide "View Full Model" and exit focal mode whenever a new model is loaded / switched
    if (this._focusResetBtn) this._focusResetBtn.style.display = 'none';
    this._inFocalMode = false;

    // Shared finaliser: applies zoom constraints, saves load-time snapshot, builds hotspots
    const finaliseCamera = (): void => {
      const loadDist = this.camera.position.distanceTo(this.controls.target);
      this.controls.maxDistance = loadDist;        // cannot orbit further than load view
      this.controls.minDistance = loadDist * 0.35; // zoom-in limit: 35 % of load distance
      this._loadMinDist = this.controls.minDistance;
      this._loadMaxDist = loadDist;
      // Snapshot BEFORE controls.update() — update() fires the 'change' event which
      // triggers clampPan(). clampPan() reads _loadTarget as the reference centre, so
      // it must already hold the correct position or it will clamp to the wrong origin.
      this._loadCamPos.copy(this.camera.position);
      this._loadTarget.copy(this.controls.target);

      this.controls.update();
      this.controls.saveState();

      // (Re)build hotspot overlays centred on the model target
      if (config.hotspots?.length) {
        this.buildHotspots(config.hotspots, this._loadTarget.clone());
      } else {
        this.clearHotspots();
      }
    };

    // ── Step 0: rotation FIRST — must happen before any bounding-box work ────
    if (config.modelRotationY !== undefined) {
      model.rotation.y = config.modelRotationY;
    }

    if (!config.cameraTarget) {
      this.scaleAndAimCamera(model, model);
      finaliseCamera();
      return;
    }

    // Find the focus node — exact name first, then case-insensitive contains
    const focusObj =
      model.getObjectByName(config.cameraTarget) ??
      this.findObjectContainingName(model, config.cameraTarget);

    if (!focusObj) {
      console.warn(`[Configurator] cameraTarget "${config.cameraTarget}" not found — using whole model`);
      this.scaleAndAimCamera(model, model);
      finaliseCamera();
      return;
    }

    // ── Step 1: measure the focus node at its native scale ──────────────────
    model.updateWorldMatrix(true, true);
    const rawBox  = new THREE.Box3().setFromObject(focusObj);
    const rawSize = rawBox.getSize(new THREE.Vector3());
    const rawMax  = Math.max(rawSize.x, rawSize.y, rawSize.z);

    // ── Step 2: scale model so focus node fills 2.5 units ───────────────────
    const scale = rawMax > 0 ? 2.5 / rawMax : 1;
    model.scale.setScalar(scale);

    // ── Step 3: floor + center based on focus node (ignores Plane etc.) ─────
    model.updateWorldMatrix(true, true);
    const scaledBox    = new THREE.Box3().setFromObject(focusObj);
    const scaledCenter = scaledBox.getCenter(new THREE.Vector3());

    model.position.x -= scaledCenter.x;
    model.position.y -= scaledBox.min.y;
    model.position.z -= scaledCenter.z;

    // ── Step 4: final world position of focus node (after model move) ────────
    model.updateWorldMatrix(true, true);
    const finalBox    = new THREE.Box3().setFromObject(focusObj);
    const finalCenter = finalBox.getCenter(new THREE.Vector3());
    const finalSize   = finalBox.getSize(new THREE.Vector3());
    const finalMax    = Math.max(finalSize.x, finalSize.y, finalSize.z);

    // ── Step 5: aim camera at focus node center ──────────────────────────────
    // 70 mm telephoto FOV is ~28° vs the old 45° FOV, so the camera must sit
    // further back by ~tan(22.5°)/tan(14°) ≈ 1.55× to frame the same object.
    // 1.75 × 1.55 ≈ 2.71; rounded to 2.7 for a clean value.
    const dist = finalMax * 4.277; // 3.888 × 1.1 — another 10 % further back
    this.camera.position.set(
      finalCenter.x,
      finalCenter.y + finalMax * 0.4,
      finalCenter.z + dist
    );
    this.controls.target.copy(finalCenter);
    finaliseCamera();
  }

  /**
   * Scales `model` so that `referenceNode`'s largest axis fills 2.5 units,
   * floors + centers the model on `referenceNode`, then aims the camera.
   * Identical formula to the seat-node path so all variants look the same size.
   */
  // ── Measurements ──────────────────────────────────────────────────────────

  /** Build (or rebuild) the W/H/D annotation for `model` at current world transform. */
  private buildDropShadow(model: THREE.Object3D): void {
    // Dispose previous
    if (this._dropShadow) {
      this.scene.remove(this._dropShadow);
      (this._dropShadow.material as THREE.MeshBasicMaterial).map?.dispose();
      (this._dropShadow.material as THREE.MeshBasicMaterial).dispose();
      this._dropShadow.geometry.dispose();
      this._dropShadow = null;
    }

    // Bounding box — exclude floor/shadow Plane nodes (same filter as measurements)
    const box = new THREE.Box3();
    model.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      if (/\bplane\b/i.test(child.name)) return;
      box.expandByObject(child);
    });
    if (box.isEmpty()) box.setFromObject(model);

    const size   = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const radius = Math.max(size.x, size.z) * 0.55;

    // Radial gradient canvas — white center fades to transparent
    const RES = 256;
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = RES;
    const ctx = canvas.getContext('2d')!;
    const grad = ctx.createRadialGradient(RES / 2, RES / 2, 0, RES / 2, RES / 2, RES / 2);
    grad.addColorStop(0.0,  'rgba(0,0,0,0.45)');
    grad.addColorStop(0.55, 'rgba(0,0,0,0.18)');
    grad.addColorStop(1.0,  'rgba(0,0,0,0.00)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, RES, RES);

    const texture  = new THREE.CanvasTexture(canvas);
    const material = new THREE.MeshBasicMaterial({
      map:         texture,
      transparent: true,
      depthWrite:  false,
      opacity:     1.0,
    });
    const geom = new THREE.CircleGeometry(radius, 64);
    const mesh = new THREE.Mesh(geom, material);
    mesh.rotation.x  = -Math.PI / 2;           // flat on the floor
    mesh.position.set(center.x, box.min.y + 0.001, center.z);
    mesh.renderOrder  = -1;
    mesh.name         = 'cfg-drop-shadow';
    this._dropShadow  = mesh;
    this.scene.add(mesh);
  }

  private buildMeasurements(model: THREE.Object3D): void {
    // Tear down previous overlay
    if (this._measureOverlay) {
      this.spriteScene.remove(this._measureOverlay.group);
      this._measureOverlay.dispose();
      this._measureOverlay = null;
    }

    this._measureOverlay = createMeasurements(model);
    this._measureOverlay.group.visible = this._measurementsVisible;
    this._measureOverlay.setSize(this.centerEl.clientWidth, this.centerEl.clientHeight);
    this.spriteScene.add(this._measureOverlay.group);

    // Keep button state in sync
    this._measureBtn?.classList.toggle('cfg-measure-btn--active', this._measurementsVisible);
  }

  /** Toggle dimension annotations on/off. */
  private toggleMeasurements(): void {
    this._measurementsVisible = !this._measurementsVisible;
    if (this._measureOverlay) {
      this._measureOverlay.group.visible = this._measurementsVisible;
    }
    this._measureBtn?.classList.toggle('cfg-measure-btn--active', this._measurementsVisible);
  }

  /** Toggle hotspot visibility on/off. */
  private toggleHotspots(): void {
    this._hotspotsEnabled = !this._hotspotsEnabled;
    this._hotspotBtn?.classList.toggle('cfg-measure-btn--active', this._hotspotsEnabled);
  }

  private scaleAndAimCamera(model: THREE.Object3D, referenceNode: THREE.Object3D): void {
    model.updateWorldMatrix(true, true);
    const rawBox  = new THREE.Box3().setFromObject(referenceNode);
    const rawSize = rawBox.getSize(new THREE.Vector3());
    const rawMax  = Math.max(rawSize.x, rawSize.y, rawSize.z);

    const scale = rawMax > 0 ? 2.5 / rawMax : 1;
    model.scale.setScalar(scale);

    model.updateWorldMatrix(true, true);
    const scaledBox    = new THREE.Box3().setFromObject(referenceNode);
    const scaledCenter = scaledBox.getCenter(new THREE.Vector3());
    model.position.x -= scaledCenter.x;
    model.position.y -= scaledBox.min.y;
    model.position.z -= scaledCenter.z;

    model.updateWorldMatrix(true, true);
    const finalBox    = new THREE.Box3().setFromObject(referenceNode);
    const finalCenter = finalBox.getCenter(new THREE.Vector3());
    const finalSize   = finalBox.getSize(new THREE.Vector3());
    const finalMax    = Math.max(finalSize.x, finalSize.y, finalSize.z);
    const dist        = finalMax * 4.277;

    this.camera.position.set(finalCenter.x, finalCenter.y + finalMax * 0.4, finalCenter.z + dist);
    this.controls.target.copy(finalCenter);
  }

  /** Case-insensitive partial-name search through the object hierarchy. */
  private findObjectContainingName(
    root: THREE.Object3D,
    name: string
  ): THREE.Object3D | null {
    const lower = name.toLowerCase();
    let found: THREE.Object3D | null = null;
    root.traverse((child) => {
      if (!found && child.name.toLowerCase().includes(lower)) found = child;
    });
    return found;
  }

  // ── NEW: Fullscreen ────────────────────────────────────────────────────────

  private toggleFullscreen(): void {
    if (!document.fullscreenElement) {
      this.container.requestFullscreen().catch((err) => {
        console.warn('[Configurator] Fullscreen request failed:', err);
      });
    } else {
      document.exitFullscreen();
    }
  }

  private onFullscreenChange(): void {
    const inFullscreen = !!document.fullscreenElement;
    const btn = this.container.querySelector<HTMLButtonElement>('#cfg-fullscreen-btn');
    if (btn) {
      const iconSvg = btn.querySelector('svg');
      btn.innerHTML = (iconSvg ? iconSvg.outerHTML : '') +
        (inFullscreen ? ' Exit Fullscreen' : ' Enter Fullscreen');
    }
    this.invalidateTAA(); // viewport size changed — stale buffer causes black frame
  }

  // ── Double-click to focus ─────────────────────────────────────────────────

  /** Mouse double-click → focus, or exit focal mode if already focused. */
  private onDoubleClick(event: MouseEvent): void {
    if (this._inFocalMode) { this.resetFocus(); return; }
    this.tryFocusAt(event.clientX, event.clientY);
  }

  /**
   * Touch double-tap detector.
   * Two taps within 300 ms and 30 px of each other trigger a focus.
   * preventDefault() stops the browser from firing a synthetic dblclick
   * or performing a page-zoom gesture on the second tap.
   */
  private onTouchEnd(event: TouchEvent): void {
    if (event.changedTouches.length !== 1) return;

    const touch = event.changedTouches[0];

    // Single tap on a hotspot sprite → focus immediately (no double-tap needed)
    if (!this._inFocalMode && this._hotspots.length > 0) {
      const rect = this.canvas.getBoundingClientRect();
      const ndc  = new THREE.Vector2(
        ((touch.clientX - rect.left) / rect.width)  *  2 - 1,
        ((touch.clientY - rect.top)  / rect.height) * -2 + 1,
      );
      this._raycaster.setFromCamera(ndc, this.camera);
      const hits = this._raycaster.intersectObjects(this._hotspots.map((h) => h.dot));
      if (hits.length > 0) {
        event.preventDefault();
        const hit = this._hotspots.find((h) => h.dot === hits[0].object);
        if (hit) { this.focusOnPoint(hit.worldPos.clone(), hit.label); return; }
      }
    }

    // Double-tap anywhere on the model → focal mode
    const now = performance.now();
    const dx  = touch.clientX - this._lastTapX;
    const dy  = touch.clientY - this._lastTapY;

    if (now - this._lastTapTime < 300 && Math.sqrt(dx * dx + dy * dy) < 30) {
      event.preventDefault();
      this._lastTapTime = 0;
      if (this._inFocalMode) { this.resetFocus(); return; }
      this.tryFocusAt(touch.clientX, touch.clientY);
    } else {
      this._lastTapTime = now;
      this._lastTapX    = touch.clientX;
      this._lastTapY    = touch.clientY;
    }
  }

  /**
   * Casts a ray from the given viewport position and, if it hits the current
   * model, smoothly flies the camera in to focus on that point.
   */
  private tryFocusAt(clientX: number, clientY: number): void {
    if (this._inFocalMode) return; // already focused — ignore; use "View Full Model" to exit
    const { currentModel } = getState();
    if (!currentModel) return;

    const rect = this.canvas.getBoundingClientRect();
    const ndc  = new THREE.Vector2(
      ((clientX - rect.left) / rect.width)  *  2 - 1,
      ((clientY - rect.top)  / rect.height) * -2 + 1
    );

    this._raycaster.setFromCamera(ndc, this.camera);
    const hits = this._raycaster.intersectObject(currentModel, true);
    if (hits.length === 0) return;

    this.focusOnPoint(hits[0].point);
  }

  /**
   * Queues a smooth camera fly-in to `point`.
   * The actual lerp runs inside tickFocusAnim(), which is called from the
   * main render loop AFTER controls.update() — so OrbitControls never
   * overrides our manually-set positions, eliminating the jerk/snap.
   */
  private focusOnPoint(point: THREE.Vector3, hotspotLabel?: string): void {
    const fromTarget = this.controls.target.clone();
    const fromCamPos = this.camera.position.clone();

    const entryOffset  = fromCamPos.clone().sub(fromTarget);
    const entryAzimuth = Math.atan2(entryOffset.x, entryOffset.z);

    const orbitDir = fromCamPos.clone().sub(fromTarget).normalize();
    const toTarget = point.clone();
    // Always use the load-time distance as the reference so scroll-zoom in full
    // model view doesn't compound with the focal zoom (0.18 × load distance).
    const toCamPos = point.clone().add(orbitDir.multiplyScalar(this._loadMaxDist * 0.18));

    if (this._focusResetBtn) this._focusResetBtn.style.display = 'flex';
    this._inFocalMode       = true;
    this._dofApertureTarget = 0; // no blur during flight

    // Lift distance constraints for the duration of the flight so
    // controls.update() cannot clamp the camera mid-lerp.
    this.controls.minDistance = 0;
    this.controls.maxDistance = Infinity;

    this._focusAnim = {
      fromTarget,
      toTarget,
      fromCamPos,
      toCamPos,
      startTime: performance.now(),
      duration:  850,
      onComplete: () => {
        const focalDist = this.camera.position.distanceTo(this.controls.target);
        this.controls.minDistance     = focalDist;
        this.controls.maxDistance     = focalDist;
        this.controls.minAzimuthAngle = entryAzimuth - Math.PI / 2;
        this.controls.maxAzimuthAngle = entryAzimuth + Math.PI / 2;
        this.controls.saveState();
        // Snap focus distance to the exact focal point before blur fades in,
        // so blur radiates outward from the focal point — never from background.
        this._autoFocusDist = focalDist;
        this._dofApertureTarget = 0.05;
        if (hotspotLabel) this.showHotspotInfo(hotspotLabel);
      },
    };
  }

  /** Queues a smooth camera fly-back to the load position. */
  private resetFocus(): void {
    const fromTarget = this.controls.target.clone();
    const fromCamPos = this.camera.position.clone();

    // Restore limits immediately so the onComplete callback has nothing to do
    // and so any residual controls state uses the correct range.
    this.controls.minDistance     = 0;
    this.controls.maxDistance     = Infinity;
    this.controls.minAzimuthAngle = -Infinity;
    this.controls.maxAzimuthAngle =  Infinity;

    this._dofApertureTarget = 0; // fade blur OUT immediately as camera flies back

    this._focusAnim = {
      fromTarget,
      toTarget:   this._loadTarget.clone(),
      fromCamPos,
      toCamPos:   this._loadCamPos.clone(),
      startTime:  performance.now(),
      duration:   850,
      onComplete: () => {
        this.controls.minDistance = this._loadMinDist;
        this.controls.maxDistance = this._loadMaxDist;
        this.controls.saveState();
        this._inFocalMode = false;
        if (this._focusResetBtn) this._focusResetBtn.style.display = 'none';
        this.hideHotspotInfo();
      },
    };
  }

  // ── Hotspot info panel ────────────────────────────────────────────────────

  private static readonly _HOTSPOT_COPY: Record<string, string> = {
    Seat:     'Ergonomically contoured cushioning with high-density foam core delivers all-day comfort. The supple full-grain leather breathes naturally, conforming to your posture over time.',
    Backrest: 'Lumbar-supportive architecture keeps the spine in neutral alignment. Stitched panel detailing adds visual depth while the structured shell maintains its shape through years of use.',
  };

  private showHotspotInfo(label: string): void {
    this.hideHotspotInfo();

    const copy = ConfiguratorApp._HOTSPOT_COPY[label]
      ?? 'Premium craftsmanship meets thoughtful design. Every detail has been considered to deliver an exceptional experience that stands the test of time.';

    // Inject Montserrat from Google Fonts once
    if (!document.getElementById('cfg-montserrat')) {
      const link = document.createElement('link');
      link.id   = 'cfg-montserrat';
      link.rel  = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap';
      document.head.appendChild(link);
    }

    const el = document.createElement('div');
    el.className = 'cfg-hotspot-info';
    el.innerHTML = `
      <p class="cfg-hotspot-info__title">${label}</p>
      <p class="cfg-hotspot-info__body">${copy}</p>
    `;
    this.centerEl.appendChild(el);
    this._hotspotInfoEl = el;
    // Trigger fade-in on next frame
    requestAnimationFrame(() => el.classList.add('cfg-hotspot-info--visible'));
  }

  private hideHotspotInfo(): void {
    const el = this._hotspotInfoEl;
    if (!el) return;
    this._hotspotInfoEl = null;
    el.classList.remove('cfg-hotspot-info--visible');
    el.addEventListener('transitionend', () => el.remove(), { once: true });
  }

  /**
   * Advances the in-flight camera animation by one frame.
   * Must be called AFTER controls.update() so that OrbitControls cannot
   * overwrite the lerped positions before they reach the renderer.
   */
  private tickFocusAnim(): void {
    if (!this._focusAnim) return;
    const { fromTarget, toTarget, fromCamPos, toCamPos, startTime, duration, onComplete } = this._focusAnim;

    const t = Math.min((performance.now() - startTime) / duration, 1);
    const e = this.easeOutCubic(t);

    this.controls.target.lerpVectors(fromTarget, toTarget, e);
    this.camera.position.lerpVectors(fromCamPos, toCamPos, e);

    if (t >= 1) {
      this._focusAnim = null;
      onComplete();
    }
  }

  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  // ── Hotspots (3-D sprites) ────────────────────────────────────────────────

  /** Canvas texture: small solid white circle. */
  private makeDotTexture(): THREE.CanvasTexture {
    const s = 64;
    const c = document.createElement('canvas');
    c.width = c.height = s;
    const ctx = c.getContext('2d')!;
    const cx = s / 2;
    ctx.beginPath();
    ctx.arc(cx, cx, cx - 2, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    return new THREE.CanvasTexture(c);
  }

  /** Canvas texture: thin white circle outline for the radiating ring. */
  private makeRingTexture(): THREE.CanvasTexture {
    const s = 128;
    const c = document.createElement('canvas');
    c.width = c.height = s;
    const ctx = c.getContext('2d')!;
    ctx.beginPath();
    ctx.arc(s / 2, s / 2, s / 2 - 4, 0, Math.PI * 2);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 4;
    ctx.stroke();
    return new THREE.CanvasTexture(c);
  }

  /** Removes all hotspot sprites from the scene and frees GPU resources. */
  private clearHotspots(): void {
    this._hotspots.forEach(({ dot, ring }) => {
      this.spriteScene.remove(dot, ring);
    });
    this._hotspotDisposables.forEach((d) => d.dispose());
    this._hotspotDisposables = [];
    this._hotspots = [];
    this._hotspotReady = false;
    this._hotspotT     = 0;
  }

  /**
   * Creates Two.js Sprites for each hotspot and adds them to the scene.
   * Sprites use `depthTest: false` so they always render on top of the model.
   * Interaction (click / tap) is handled via raycasting against the dot sprites.
   */
  private buildHotspots(hotspots: Hotspot[], origin: THREE.Vector3): void {
    this.clearHotspots();

    const dotTex  = this.makeDotTexture();
    const ringTex = this.makeRingTexture();
    this._hotspotDisposables.push(dotTex, ringTex);

    hotspots.forEach(({ label, offset }) => {
      const worldPos = new THREE.Vector3(
        origin.x + offset[0],
        origin.y + offset[1],
        origin.z + offset[2],
      );

      // Dot
      const dotMat = new THREE.SpriteMaterial({ map: dotTex, depthTest: false, transparent: true });
      const dot    = new THREE.Sprite(dotMat);
      dot.position.copy(worldPos);
      dot.scale.setScalar(0.09);
      dot.renderOrder = 10;
      this.spriteScene.add(dot);

      // Radiating ring
      const ringMat = new THREE.SpriteMaterial({ map: ringTex, depthTest: false, transparent: true, opacity: 0.8 });
      const ring    = new THREE.Sprite(ringMat);
      ring.position.copy(worldPos);
      ring.scale.setScalar(0.09);
      ring.renderOrder = 9;
      this.spriteScene.add(ring);

      this._hotspotDisposables.push(dotMat, ringMat);
      this._hotspots.push({ label, worldPos, dot, ring });
    });

    // Start popup animation from zero
    this._hotspotT     = 0;
    this._hotspotReady = true;
  }

  /** Animates hotspot show/hide (popup spring) and the pulse ring. */
  private updateHotspots(): void {
    if (this._hotspots.length === 0) return;

    // Visible only when enabled, ready, guide dismissed, morph done, and not in focal mode
    const target = (
      this._hotspotsEnabled && this._hotspotReady && !this._guideActive &&
      !this._morphActive && !this._inFocalMode
    ) ? 1 : 0;

    // Same lerp rate in both directions — identical feel for show and hide
    this._hotspotT += (target - this._hotspotT) * 0.10;

    const t        = Math.max(0, Math.min(this._hotspotT, 1));
    const DOT_BASE = 0.09;
    const phase    = (Date.now() % 2000) / 2000;

    this._hotspots.forEach(({ dot, ring }) => {
      const invisible = t < 0.01;
      dot.visible  = !invisible;
      ring.visible = !invisible && t > 0.5;

      if (invisible) return;

      dot.scale.setScalar(DOT_BASE * t);
      (dot.material as THREE.SpriteMaterial).opacity = t;

      if (ring.visible) {
        ring.scale.setScalar(DOT_BASE * (1 + phase * 1.8));
        (ring.material as THREE.SpriteMaterial).opacity = (1 - phase) * 0.8 * t;
      }
    });
  }

  /**
   * Single-click handler: focuses on a hotspot if the click lands on its dot sprite.
   * Does nothing if in focal mode (hotspots are hidden then).
   */
  private onSingleClick(event: MouseEvent): void {
    if (this._inFocalMode || this._hotspots.length === 0) return;

    const rect = this.canvas.getBoundingClientRect();
    const ndc  = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width)  *  2 - 1,
      ((event.clientY - rect.top)  / rect.height) * -2 + 1,
    );
    this._raycaster.setFromCamera(ndc, this.camera);
    const hits = this._raycaster.intersectObjects(this._hotspots.map((h) => h.dot));
    if (hits.length === 0) return;

    const hit = this._hotspots.find((h) => h.dot === hits[0].object);
    if (hit) this.focusOnPoint(hit.worldPos.clone(), hit.label);
  }

  // ── Rotation hint ─────────────────────────────────────────────────────────

  /**
   * Plays a gentle left-right orbit swing to hint the model is rotatable.
   * Driven from the render loop (tickHintAnim) so it runs after controls.update().
   */
  private playRotationHint(): void {
    if (this._inFocalMode || this._loadMaxDist === 0) return;

    const arrowEl = this.buildHintArrow();
    this.centerEl.appendChild(arrowEl);
    // Fade in on next frame
    requestAnimationFrame(() => { arrowEl.style.opacity = '1'; });

    const DURATION = 2600;
    this._hintAnim = {
      baseCamPos: this.camera.position.clone(),
      pivot:      this.controls.target.clone(),
      startTime:  performance.now(),
      duration:   DURATION,
      arrowEl,
    };
  }

  private buildHintArrow(): HTMLElement {
    // Inject styles once
    if (!document.getElementById('hint-anim-styles')) {
      const s = document.createElement('style');
      s.id = 'hint-anim-styles';
      s.textContent = `
        .hint-wrap {
          position: absolute;
          bottom: 14%;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 14px;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.55s ease;
          z-index: 100;
          user-select: none;
        }
        .hint-row {
          display: flex;
          align-items: center;
          gap: 18px;
        }
        /* Rotation arc arrows */
        .hint-arc svg {
          display: block;
          filter: drop-shadow(0 1px 6px rgba(0,0,0,0.55));
        }
        /* Double-tap indicator */
        .hint-tap {
          position: relative;
          width: 56px;
          height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .hint-tap-dot {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: rgba(255,255,255,0.92);
          box-shadow: 0 0 0 3px rgba(255,255,255,0.18);
          position: relative;
          z-index: 2;
          animation: hint-dot-tap 2.6s ease-out both;
        }
        .hint-tap-ring {
          position: absolute;
          border-radius: 50%;
          border: 1.5px solid rgba(255,255,255,0.75);
          width: 18px;
          height: 18px;
          opacity: 0;
        }
        /* Tap 1 rings */
        .hint-ring-a { animation: hint-ring-expand 0.85s ease-out 0.35s both; }
        .hint-ring-b { animation: hint-ring-expand 0.85s ease-out 0.50s both; }
        /* Tap 2 rings */
        .hint-ring-c { animation: hint-ring-expand 0.85s ease-out 1.20s both; }
        .hint-ring-d { animation: hint-ring-expand 0.85s ease-out 1.35s both; }

        @keyframes hint-dot-tap {
          /* tap 1 at ~0.35s = 13.5% of 2.6s */
          0%, 11%           { transform: scale(1); }
          15%               { transform: scale(0.68); }
          24%               { transform: scale(1); }
          /* tap 2 at ~1.20s = 46% of 2.6s */
          43%               { transform: scale(1); }
          47%               { transform: scale(0.68); }
          56%               { transform: scale(1); }
          100%              { transform: scale(1); }
        }
        @keyframes hint-ring-expand {
          0%   { transform: scale(1);   opacity: 0.65; }
          100% { transform: scale(3.6); opacity: 0;    }
        }
        /* Labels row */
        .hint-labels {
          display: flex;
          align-items: center;
          gap: 12px;
          font: 500 10px/1 system-ui, -apple-system, sans-serif;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.60);
          text-shadow: 0 1px 5px rgba(0,0,0,0.7);
          white-space: nowrap;
        }
        .hint-label-sep { opacity: 0.30; font-size: 14px; line-height: 1; }
      `;
      document.head.appendChild(s);
    }

    // ── Left rotation arc (counter-clockwise, arrowhead at bottom-left) ──────
    // Bezier curve: starts top-right, sweeps left and downward
    const LEFT_ARC = `<svg width="46" height="52" viewBox="0 0 46 52" fill="none">
      <path d="M 40 6 C 18 4 4 16 4 30 C 4 36 7 41 11 45"
            stroke="rgba(255,255,255,0.88)" stroke-width="2.4" stroke-linecap="round" fill="none"/>
      <path d="M 3 35 L 11 45 L 20 40"
            stroke="rgba(255,255,255,0.88)" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    </svg>`;

    // ── Right rotation arc (clockwise, mirrored) ─────────────────────────────
    const RIGHT_ARC = `<svg width="46" height="52" viewBox="0 0 46 52" fill="none">
      <path d="M 6 6 C 28 4 42 16 42 30 C 42 36 39 41 35 45"
            stroke="rgba(255,255,255,0.88)" stroke-width="2.4" stroke-linecap="round" fill="none"/>
      <path d="M 43 35 L 35 45 L 26 40"
            stroke="rgba(255,255,255,0.88)" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    </svg>`;

    const wrap = document.createElement('div');
    wrap.className = 'hint-wrap';
    wrap.innerHTML = `
      <div class="hint-row">
        <div class="hint-arc">${LEFT_ARC}</div>
        <div class="hint-tap">
          <div class="hint-tap-ring hint-ring-a"></div>
          <div class="hint-tap-ring hint-ring-b"></div>
          <div class="hint-tap-ring hint-ring-c"></div>
          <div class="hint-tap-ring hint-ring-d"></div>
          <div class="hint-tap-dot"></div>
        </div>
        <div class="hint-arc">${RIGHT_ARC}</div>
      </div>
      <div class="hint-labels">
        <span>Drag to Rotate</span>
        <span class="hint-label-sep">·</span>
        <span>Double&#8209;click to Focus</span>
      </div>
    `;
    return wrap;
  }

  /**
   * Advances the hint swing animation one frame.
   * Uses sin(t·2π) so the camera sweeps left → centre → right → centre → stop,
   * ending exactly at the original position.
   * Must be called AFTER controls.update().
   */
  private tickHintAnim(): void {
    if (!this._hintAnim || this._focusAnim) return;
    const { baseCamPos, pivot, startTime, duration, arrowEl } = this._hintAnim;

    const t = Math.min((performance.now() - startTime) / duration, 1);

    // Full sin cycle: 0 → −peak → 0 → +peak → 0; keeps the camera at origin at t=1
    const MAX_ANGLE = Math.PI / 8; // 22.5° each side
    const angle = MAX_ANGLE * Math.sin(t * Math.PI * 2) * (1 - t); // dampen toward end

    const ox = baseCamPos.x - pivot.x;
    const oz = baseCamPos.z - pivot.z;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    this.camera.position.set(
      pivot.x + ox * cos - oz * sin,
      baseCamPos.y,
      pivot.z + ox * sin + oz * cos,
    );
    this.camera.lookAt(pivot);

    // Fade arrow out in the last quarter
    if (t > 0.75) {
      arrowEl.style.opacity = String(1 - (t - 0.75) / 0.25);
    }

    if (t >= 1) {
      this.camera.position.copy(baseCamPos);
      this.camera.lookAt(pivot);
      arrowEl.remove();
      this._hintAnim = null;
    }
  }

  // ── AA mode control ───────────────────────────────────────────────────────

  setAAMode(mode: 'off' | 'smaa' | 'tsaa'): void {
    this._aaMode = mode;
    // SMAA runs every frame for both 'smaa' and 'tsaa' modes — covers moving frames
    // that TAA accumulation can't help with.
    this.smaaPass.enabled       = mode !== 'off';
    this.taaPass.accumulate     = false; // reset; render loop manages this for tsaa
    this.taaPass.enabled        = mode !== 'off';
    const aaSelect = this.centerEl.querySelector<HTMLSelectElement>('#cfg-aa-select');
    if (aaSelect) aaSelect.value = mode;
  }

  // ── Depth of field ────────────────────────────────────────────────────────

  /**
   * Autofocus depth-of-field:
   *
   * Every frame, casts a ray from the centre of the camera view and finds
   * the nearest mesh surface. That surface distance becomes the focal plane,
   * so whatever geometry the camera is pointing at stays sharp while
   * everything else blurs — even if the camera orbits and brings a
   * different part (e.g. an armrest) in front of the lens.
   *
   * A per-frame lerp (factor 0.08) smooths the focal distance so the
   * focus plane glides rather than snapping when the nearest surface changes.
   *
   * Blur intensity (aperture) is still driven by zoom level:
   *   zoomed-out (> 70 % of range) → no blur
   *   zoomed-in  (< 30 % of range) → full blur  (aperture 0.05, maxblur 0.025)
   *   between                       → smoothstep blend
   */
  private updateDoF(): void {
    if (this._loadMaxDist === 0) return; // model not yet loaded

    const u = (this.bokehPass as unknown as { uniforms: Record<string, { value: number }> }).uniforms;

    // Fade out faster than fade in — exiting focal mode should feel snappy,
    // entering should be more graceful.
    const lerpFactor = this._dofApertureTarget < this._dofApertureCurrent ? 0.14 : 0.055;
    this._dofApertureCurrent += (this._dofApertureTarget - this._dofApertureCurrent) * lerpFactor;

    // Snap to zero below noise floor to avoid micro-blur when fully faded out
    if (this._dofApertureCurrent < 0.0004) this._dofApertureCurrent = 0;

    u['aperture'].value = this._dofApertureCurrent;

    // No need to run autofocus if blur is fully off
    if (this._dofApertureCurrent === 0) return;

    // ── Autofocus: raycast from screen centre to nearest surface ─────────────
    const { currentModel } = getState();
    if (currentModel) {
      this._raycaster.setFromCamera(new THREE.Vector2(0, 0), this.camera);
      const hits = this._raycaster.intersectObject(currentModel, true);
      const targetFocus = hits.length > 0
        ? hits[0].distance
        : this.camera.position.distanceTo(this.controls.target);
      this._autoFocusDist += (targetFocus - this._autoFocusDist) * 0.08;
    }

    u['focus'].value = this._autoFocusDist;
  }

  // ── Variant cross-fade morph ──────────────────────────────────────────────

  /** Sets opacity on every StandardMaterial mesh in `obj`, enabling transparency as needed. */
  private _setModelOpacity(obj: THREE.Object3D, opacity: number): void {
    obj.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const mats = Array.isArray(child.material)
        ? (child.material as THREE.MeshStandardMaterial[])
        : [child.material as THREE.MeshStandardMaterial];
      mats.forEach((m) => {
        if (!('opacity' in m)) return;
        m.transparent = opacity < 1;
        m.opacity     = opacity;
        m.needsUpdate = true;
      });
    });
  }

  /**
   * Per-frame cross-fade driver.
   * New model fades 0→1 over ~16 frames. Old model stays fully opaque as a
   * backdrop (avoids two semi-transparent meshes → depth-sort bleed-through).
   * When done: removes + disposes old model, resets new model to fully opaque.
   */
  private tickMorph(): void {
    if (!this._morphActive) return;

    this._morphFadeIn = Math.min(1, this._morphFadeIn + 0.07);
    if (this._morphNewModel) this._setModelOpacity(this._morphNewModel, this._morphFadeIn);

    if (this._morphFadeIn >= 1) {
      if (this._morphOldModel) {
        this.scene.remove(this._morphOldModel);
        disposeObject(this._morphOldModel);
        this._morphOldModel = null;
      }
      if (this._morphNewModel) {
        this._setModelOpacity(this._morphNewModel, 1);
        this._morphNewModel = null;
      }
      this._morphActive = false;
      this.hideVariantLoader();
      this.invalidateTAA();
    }
  }

  // ── TAA flush ─────────────────────────────────────────────────────────────

  /**
   * Forces `taaPass.accumulate = false` for the next N frames so the stale
   * accumulated buffer is never blended into a freshly changed scene.
   * Called after model loads, variant swaps, fullscreen changes, and resizes.
   */
  private invalidateTAA(): void {
    this._taaResetFrames = 45;
  }

  // ── Render loop ───────────────────────────────────────────────────────────

  private startLoop(): void {
    const tick = (): void => {
      this.rafId = requestAnimationFrame(tick);
      this.controls.update();
      this.tickFocusAnim(); // apply lerp AFTER controls.update() so it can't be overridden
      this.tickHintAnim();  // rotation hint swing — also runs after controls.update()
      this.tickMorph();     // cross-fade between variant models

      // TAA accumulation: only active in 'tsaa' mode and only when camera is still.
      const { x, y, z } = this.camera.position;
      const moved = Math.abs(x - this._prevCamX) > 1e-7 ||
                    Math.abs(y - this._prevCamY) > 1e-7 ||
                    Math.abs(z - this._prevCamZ) > 1e-7;
      const taaLocked = this._taaResetFrames > 0;
      if (taaLocked) this._taaResetFrames--;
      this.taaPass.accumulate = this._aaMode === 'tsaa' && !moved && !taaLocked;
      this._prevCamX = x; this._prevCamY = y; this._prevCamZ = z;

      this.updateDoF();
      this.updateHotspots();
      this._debugUpdate?.();
      this.composer.render();
      // Update measurement label rotations to stay parallel to their lines
      if (this._measureOverlay) {
        const vw = this.centerEl.clientWidth;
        const vh = this.centerEl.clientHeight;
        this._measureOverlay.update(this.camera, vw, vh);
      }
      // Render sprites on top without clearing — bypasses GTAO/DoF/OutputPass entirely
      this.renderer.autoClear = false;
      this.renderer.render(this.spriteScene, this.camera);
      this.renderer.autoClear = true;
    };
    tick();
  }

  // ── Cleanup ───────────────────────────────────────────────────────────────

  /**
   * Tears down the configurator: cancels the render loop, disconnects
   * observers, and frees GPU resources.
   */
  destroy(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);

    this.resizeObserver.disconnect();

    const { currentModel } = getState();
    if (currentModel) {
      this.scene.remove(currentModel);
      disposeObject(currentModel);
    }

    this.clearHotspots();
    this.renderer.dispose();
    this.controls.dispose();
    document.removeEventListener('fullscreenchange', this._onFullscreenChange);
    this.canvas.removeEventListener('click',     this._onClick);
    this.canvas.removeEventListener('dblclick',  this._onDblClick);
    this.canvas.removeEventListener('touchend',  this._onTouchEnd);
    this.container.classList.remove('cfg-wrapper');
    this.container.innerHTML = '';
  }
}
